/**
 * Asset Configuration Utility
 * Configuration maps for each campaign type and entity level
 */

import type {
  AssetLevelConfig,
  TextAssetType,
  ImageAssetType,
  EntityType,
  AssetGroupData,
  AdGroupData,
} from '../types';
import { CAMPAIGN_LEVEL_ID } from '../types';

// Campaign type configuration
interface CampaignTypeConfig {
  campaignLevelTextAssets: TextAssetType[];
  campaignLevelImageTypes: ImageAssetType[];
  entityType: EntityType;
  entityLabel: string;
  entityLevelTextAssets: TextAssetType[];
  entityLevelImageTypes: ImageAssetType[];
  supportsRSA: boolean;
  supportsRDA: boolean;  // Responsive Display Ad editing (for Display campaigns)
}

const CAMPAIGN_TYPE_CONFIGS: Record<string, CampaignTypeConfig> = {
  PERFORMANCE_MAX: {
    campaignLevelTextAssets: ['sitelink', 'callout', 'structured_snippet'],
    campaignLevelImageTypes: [], // PMax campaign level doesn't support images
    entityType: 'asset_group',
    entityLabel: 'Asset Group',
    entityLevelTextAssets: ['headline', 'description', 'long_headline'],
    entityLevelImageTypes: [
      'marketing_image',
      'square_marketing_image',
      'portrait_marketing_image',
      'logo',
      'landscape_logo',
    ],
    supportsRSA: false,
    supportsRDA: false,
  },
  SEARCH: {
    campaignLevelTextAssets: ['sitelink', 'callout', 'structured_snippet'],
    campaignLevelImageTypes: ['marketing_image', 'square_marketing_image'], // Image assets for Search ads
    entityType: 'ad_group',
    entityLabel: 'Ad Group',
    // Note: sitelinks/callouts require campaign-level linking via AdGroupAsset API (not yet implemented)
    entityLevelTextAssets: ['headline', 'description'],
    entityLevelImageTypes: ['marketing_image', 'square_marketing_image'], // Image assets at ad group level
    supportsRSA: true,
    supportsRDA: false,
  },
  DISPLAY: {
    campaignLevelTextAssets: ['sitelink', 'callout', 'structured_snippet'],
    campaignLevelImageTypes: ['marketing_image', 'square_marketing_image'],
    entityType: 'ad_group',
    entityLabel: 'Ad Group',
    // Note: Display campaigns do NOT support headline/description at ad group level via AdGroupAsset API.
    // Headlines/descriptions for Display are embedded directly in the ResponsiveDisplayAd (RDA).
    // Sitelinks/callouts also require campaign-level linking (not yet implemented).
    entityLevelTextAssets: [],  // No text assets supported at ad group level for Display
    entityLevelImageTypes: ['marketing_image', 'square_marketing_image'],
    supportsRSA: false,
    supportsRDA: true,  // Display campaigns use RDAs instead of RSAs
  },
  SHOPPING: {
    campaignLevelTextAssets: ['sitelink', 'callout'],
    campaignLevelImageTypes: [],
    entityType: 'ad_group',
    entityLabel: 'Ad Group',
    entityLevelTextAssets: [],
    entityLevelImageTypes: [],
    supportsRSA: false,
    supportsRDA: false,
  },
  AI_MAX_SEARCH: {
    campaignLevelTextAssets: ['sitelink', 'callout', 'structured_snippet'],
    campaignLevelImageTypes: ['marketing_image', 'square_marketing_image'],
    entityType: 'ad_group',
    entityLabel: 'Ad Group',
    // Note: sitelinks/callouts require campaign-level linking via AdGroupAsset API (not yet implemented)
    entityLevelTextAssets: ['headline', 'description'],
    entityLevelImageTypes: ['marketing_image', 'square_marketing_image'],
    supportsRSA: true,
    supportsRDA: false,
  },
  DEMAND_GEN: {
    campaignLevelTextAssets: ['sitelink', 'callout'],
    campaignLevelImageTypes: [],
    entityType: 'ad_group',
    entityLabel: 'Ad Group',
    // Demand Gen text/image assets are embedded in ads (Multi-Asset, Carousel, Video)
    // NOT supported via AdGroupAssetService - images must be added through ad creation/editing
    // AdGroupAssetService returns FIELD_TYPE_INCOMPATIBLE_WITH_CAMPAIGN_TYPE for AD_IMAGE
    entityLevelTextAssets: [],
    entityLevelImageTypes: [],  // No ad-group-level image linking for Demand Gen
    supportsRSA: false,
    supportsRDA: false,
  },
};

/**
 * Get the configuration for a campaign type
 */
export function getCampaignTypeConfig(campaignType: string): CampaignTypeConfig | undefined {
  // Normalize the campaign type
  const normalizedType = campaignType.toUpperCase().replace(/\s+/g, '_');

  // Try exact match first
  if (CAMPAIGN_TYPE_CONFIGS[normalizedType]) {
    return CAMPAIGN_TYPE_CONFIGS[normalizedType];
  }

  // Try partial match
  for (const [key, config] of Object.entries(CAMPAIGN_TYPE_CONFIGS)) {
    if (normalizedType.includes(key) || key.includes(normalizedType)) {
      return config;
    }
  }

  return undefined;
}

/**
 * Check if a campaign type supports assets
 */
export function supportsAssets(campaignType: string | undefined): boolean {
  if (!campaignType) return false;
  return getCampaignTypeConfig(campaignType) !== undefined;
}

/**
 * Get available asset levels for a campaign
 */
export function getAvailableAssetLevels(
  campaignType: string,
  campaignId: string,
  assetGroups?: AssetGroupData[],
  adGroups?: AdGroupData[]
): AssetLevelConfig[] {
  const config = getCampaignTypeConfig(campaignType);
  if (!config) return [];

  const levels: AssetLevelConfig[] = [];

  // Campaign level is always available
  levels.push({
    id: CAMPAIGN_LEVEL_ID,
    name: 'Campaign level',
    type: 'campaign',
    supportedTextAssets: config.campaignLevelTextAssets,
    supportedImageTypes: config.campaignLevelImageTypes,
  });

  // Add entity-level options based on campaign type
  // Exclude REMOVED entities from dropdown
  if (config.entityType === 'asset_group' && assetGroups) {
    assetGroups
      .filter((ag) => ag.status !== 'REMOVED')
      .forEach((ag) => {
        levels.push({
          id: ag.asset_group_id,
          name: `${ag.asset_group_name} (Asset Group level)`,
          type: 'asset_group',
          supportedTextAssets: config.entityLevelTextAssets,
          supportedImageTypes: config.entityLevelImageTypes,
        });
      });
  } else if (config.entityType === 'ad_group' && adGroups) {
    adGroups
      .filter((ag) => ag.status !== 'REMOVED')
      .forEach((ag) => {
        const adGroupId = ag.ad_group_id || ag.ads_group_id || '';
        const adGroupName = ag.ad_group_name || ag.ads_group_name || 'Unnamed';
        levels.push({
          id: adGroupId,
          name: `${adGroupName} (Ad Group level)`,
          type: 'ad_group',
          supportedTextAssets: config.entityLevelTextAssets,
          supportedImageTypes: config.entityLevelImageTypes,
        });
      });
  }

  return levels;
}

/**
 * Get supported text asset types for a specific level
 */
export function getSupportedTextAssets(
  campaignType: string,
  levelType: EntityType
): TextAssetType[] {
  const config = getCampaignTypeConfig(campaignType);
  if (!config) return [];

  if (levelType === 'campaign') {
    return config.campaignLevelTextAssets;
  }
  return config.entityLevelTextAssets;
}

/**
 * Get supported image types for a specific level
 */
export function getSupportedImageTypes(
  campaignType: string,
  levelType: EntityType
): ImageAssetType[] {
  const config = getCampaignTypeConfig(campaignType);
  if (!config) return [];

  if (levelType === 'campaign') {
    return config.campaignLevelImageTypes;
  }
  return config.entityLevelImageTypes;
}

/**
 * Check if RSA editing is supported for a campaign type
 */
export function supportsRSAEditing(campaignType: string): boolean {
  const config = getCampaignTypeConfig(campaignType);
  return config?.supportsRSA ?? false;
}

/**
 * Check if images are supported at a specific level
 */
export function supportsImages(campaignType: string, levelType: EntityType): boolean {
  const imageTypes = getSupportedImageTypes(campaignType, levelType);
  return imageTypes.length > 0;
}

/**
 * Get the entity type for a campaign
 */
export function getEntityType(campaignType: string): EntityType | undefined {
  const config = getCampaignTypeConfig(campaignType);
  return config?.entityType;
}

/**
 * Get the entity label for a campaign type
 */
export function getEntityLabel(campaignType: string): string {
  const config = getCampaignTypeConfig(campaignType);
  return config?.entityLabel || 'Entity';
}

/**
 * Check if a campaign has any entities (ad groups or asset groups)
 * Excludes REMOVED entities from the count
 */
export function hasEntities(
  campaignType: string,
  assetGroups?: AssetGroupData[],
  adGroups?: AdGroupData[]
): boolean {
  const config = getCampaignTypeConfig(campaignType);
  if (!config) return false;

  if (config.entityType === 'asset_group') {
    const activeAssetGroups = assetGroups?.filter((ag) => ag.status !== 'REMOVED') ?? [];
    return activeAssetGroups.length > 0;
  }
  const activeAdGroups = adGroups?.filter((ag) => ag.status !== 'REMOVED') ?? [];
  return activeAdGroups.length > 0;
}

/**
 * Get RSAs from an ad group (excludes REMOVED ads)
 */
export function getRSAsFromAdGroup(adGroup: AdGroupData): Array<{
  ad_id: string;
  name: string;
  headlines: string[];
}> {
  const ads = adGroup.ads_list || adGroup.ads || [];
  return ads
    .filter((ad) => ad.ad_type === 'RESPONSIVE_SEARCH_AD' && ad.status !== 'REMOVED')
    .map((ad) => ({
      ad_id: ad.ad_id,
      name: ad.ad_name || `RSA ${ad.ad_id}`,
      headlines: (ad.headlines || []).map((h) =>
        typeof h === 'string' ? h : h.text
      ).slice(0, 3), // Preview first 3 headlines
    }));
}

/**
 * Check if RDA editing is supported for a campaign type
 */
export function supportsRDAEditing(campaignType: string): boolean {
  const config = getCampaignTypeConfig(campaignType);
  return config?.supportsRDA ?? false;
}

/**
 * Get RDAs (Responsive Display Ads) from an ad group (excludes REMOVED ads)
 */
export function getRDAsFromAdGroup(adGroup: AdGroupData): Array<{
  ad_id: string;
  name: string;
  headlines: string[];
  long_headline?: string;
  business_name?: string;
}> {
  const ads = adGroup.ads_list || adGroup.ads || [];
  return ads
    .filter((ad) => ad.ad_type === 'RESPONSIVE_DISPLAY_AD' && ad.status !== 'REMOVED')
    .map((ad) => {
      // Headlines can be strings or objects with a text property
      const headlines = (ad.headlines || []).map((h) =>
        typeof h === 'string' ? h : h.text
      ).slice(0, 3); // Preview first 3 headlines
      return {
        ad_id: ad.ad_id,
        name: ad.ad_name || `RDA ${ad.ad_id}`,
        headlines,
        long_headline: ad.long_headline,
        business_name: ad.business_name,
      };
    });
}
